import React from 'react'
import { string, shape, arrayOf, func } from 'prop-types'

import { Row, Col } from '@gympass/yoga'
import { useIntl } from 'react-intl'

import DynamicCheckbox from './Components/DynamicCheckbox'
import DynamicInput from './Components/DynamicInput'
import DynamicSelect from './Components/DynamicSelect'
import DynamicTextBlock from './Components/DynamicTextBlock'

const GRID_SIZE = 12
const DEFAULT_COLUMN_SIZE = 6

function getDynamicField(
  field,
  register,
  control,
  disabledFields,
  errors,
  setValue,
  clearErrors,
  getValues,
  setFunnelStage
) {
  const intl = useIntl()
  const elem = { ...field }
  elem.originalLabel = elem.label
  elem.label = intl.messages[elem.label]
  const isDisabled = disabledFields[field.name]
  const key = `dynamic-${field.id}`

  if (elem.componentType === 'input') {
    return (
      <Col key={key} xxs={elem.columnSize.small} md={elem.columnSize.medium}>
        <DynamicInput
          field={elem}
          register={register}
          disabled={isDisabled}
          errors={errors[elem.name]}
          control={control}
        />
      </Col>
    )
  }
  if (elem.componentType === 'checkbox') {
    return (
      <Col key={key} xxs={elem.columnSize.small} md={elem.columnSize.medium}>
        <DynamicCheckbox
          field={elem}
          register={register}
          errors={errors[elem.name]}
        />
      </Col>
    )
  }
  if (elem.componentType === 'select') {
    if (elem.items.translation) {
      elem.items.defaultElements.elements.forEach(e => {
        e.name = intl.messages[e.intlKey]
      })
    }
    return (
      <Col key={key} xxs={elem.columnSize.small} md={elem.columnSize.medium}>
        <DynamicSelect
          field={elem}
          register={register}
          errors={errors[elem.name]}
          control={control}
          setValue={setValue}
          clearErrors={clearErrors}
          getValues={getValues}
          setFunnelStage={setFunnelStage}
        />
      </Col>
    )
  }
  if (elem.componentType === 'text') {
    return (
      <Col key={key} xxs={elem.columnSize.small} md={elem.columnSize.medium}>
        <DynamicTextBlock field={elem} />
      </Col>
    )
  }
  return null
}

function getColumnSize(column) {
  return (
    column.columnSize.large ||
    column.columnSize.medium ||
    column.columnSize.small ||
    DEFAULT_COLUMN_SIZE
  )
}

function createAndInsertRow(rows, elements) {
  const key = `row-${rows.length}`
  const row = (
    <Row key={key} data-testid="dynamic-form-row">
      {elements}
    </Row>
  )
  rows.push(row)
  return rows
}

function resetRow() {
  return { currentRow: [], currentRowSize: 0 }
}

function mountRows(
  fields,
  register,
  disabledFields,
  errors,
  control,
  setValue,
  clearErrors,
  getValues,
  setFunnelStage
) {
  let rows = []
  let currentRow = []
  let currentRowSize = 0

  fields.forEach(field => {
    const columnSize = getColumnSize(field)
    if (columnSize + currentRowSize > GRID_SIZE) {
      rows = createAndInsertRow(rows, currentRow)
      const reset = resetRow()
      currentRow = reset.currentRow
      currentRowSize = reset.currentRowSize
    }
    const col = getDynamicField(
      field,
      register,
      control,
      disabledFields,
      errors,
      setValue,
      clearErrors,
      getValues,
      setFunnelStage
    )
    currentRow.push(col)
    currentRowSize += columnSize
    if (field.breaklineAfter) {
      rows = createAndInsertRow(rows, currentRow)
      const reset = resetRow()
      currentRow = reset.currentRow
      currentRowSize = reset.currentRowSize
    }
  })

  if (currentRow.length > 0) {
    rows = createAndInsertRow(rows, currentRow)
  }
  return rows
}

export default function DynamicForm({
  fields,
  register,
  disabledFields,
  errors,
  control,
  setValue,
  clearErrors,
  getValues,
  setFunnelStage,
}) {
  const components = mountRows(
    fields,
    register,
    disabledFields,
    errors,
    control,
    setValue,
    clearErrors,
    getValues,
    setFunnelStage
  )
  return components
}

DynamicForm.propTypes = {
  fields: arrayOf(
    shape({
      label: string,
    })
  ).isRequired,
  register: func.isRequired,
  control: shape({}).isRequired,
  disabledFields: shape({}),
  errors: shape({}),
}

DynamicForm.defaultProps = {
  disabledFields: {},
  errors: {},
}
